import { inject, Injectable } from '@angular/core';

import { AlertController } from '@ionic/angular';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { select, Store } from '@ngrx/store';

import { map, of, switchMap, withLatestFrom } from 'rxjs';
import { catchError, concatMap } from 'rxjs/operators';

import { AdminPortalApiService } from '../../misc/services/adminPortalApi.service';
import { FileService } from '../../misc/services/file.service';
import { ReportApiService } from '../../misc/services/reportApi.service';
import { AmortizationReportData } from '../../typings/calculator';
import * as fromCalculatorActions from '../actions/calculator.actions';
import * as fromCalculatorSelectors from '../selectors/calculator.selectors';
import * as fromSettingSelectors from '../selectors/setting.selectors';
import * as fromUserSelectors from '../selectors/user.selectors';

@Injectable()
export class CalculatorEffects {
  private actions$ = inject(Actions);
  private adminPortalApiService = inject(AdminPortalApiService);
  private alertController = inject(AlertController);
  private fileService = inject(FileService);
  private reportApiService = inject(ReportApiService);
  private store = inject(Store);

  savePITIReport$ = createEffect(() =>
    this.actions$.pipe(
      ofType(fromCalculatorActions.savePITIReport),
      withLatestFrom(
        this.store.pipe(select(fromUserSelectors.selectId)),
        this.store.pipe(select(fromSettingSelectors.selectReferralCode))
      ),
      switchMap(
        ([
          {
            payload: { reportData }
          },
          userId,
          referralCode
        ]) =>
          this.reportApiService
            .sendRequest('POST', '/v3/Report/Calculator/PITI', {
              body: {
                ...reportData,
                parseUserId: userId,
                repCode: referralCode
              },
              responseType: 'blob'
            })
            .pipe(
              switchMap(async (blob: Blob) => {
                await this.fileService.exportDocument(
                  blob,
                  'calculator-payment-export.pdf',
                  'Calculator payment export'
                );
                return fromCalculatorActions.savePITIReportSuccess();
              }),
              catchError((error: Error) => of(fromCalculatorActions.savePITIReportFailure({ payload: { error } })))
            )
      )
    )
  );

  saveAffordabilityReport$ = createEffect(() =>
    this.actions$.pipe(
      ofType(fromCalculatorActions.saveAffordabilityReport),
      withLatestFrom(
        this.store.pipe(select(fromUserSelectors.selectId)),
        this.store.pipe(select(fromSettingSelectors.selectReferralCode))
      ),
      switchMap(
        ([
          {
            payload: { reportData }
          },
          userId,
          referralCode
        ]) =>
          this.reportApiService
            .sendRequest('POST', '/v3/Report/Calculator/Affordability', {
              body: {
                ...reportData,
                parseUserId: userId,
                repCode: referralCode
              },
              responseType: 'blob'
            })
            .pipe(
              switchMap(async (blob: Blob) => {
                await this.fileService.exportDocument(
                  blob,
                  'calculator-affordability-export.pdf',
                  'Calculator affordability export'
                );
                return fromCalculatorActions.saveAffordabilityReportSuccess();
              }),
              catchError((error: Error) =>
                of(fromCalculatorActions.saveAffordabilityReportFailure({ payload: { error } }))
              )
            )
      )
    )
  );

  updateAmortizationCalculatorData$ = createEffect(() =>
    this.actions$.pipe(ofType(fromCalculatorActions.updateAmortizationCalculatorData)).pipe(
      withLatestFrom(this.store.pipe(select(fromCalculatorSelectors.selectAmortizationMortgageTerm))),
      switchMap(([{ payload }, term]) =>
        this.adminPortalApiService
          .sendRequest('GET', '/v1/Calculators/Amortization', {
            params: this.adminPortalApiService.getQueryParams({
              amount: payload.amount,
              interestRate: payload.interestRate,
              term
            })
          })
          .pipe(
            map((amortizationReportData: AmortizationReportData) =>
              fromCalculatorActions.getAmortizationDataSuccess({ payload: { reportData: amortizationReportData } })
            )
          )
      )
    )
  );

  saveAmortizationReport$ = createEffect(() =>
    this.actions$.pipe(
      ofType(fromCalculatorActions.saveAmortizationReport),
      withLatestFrom(
        this.store.pipe(select(fromUserSelectors.selectId)),
        this.store.pipe(select(fromSettingSelectors.selectReferralCode))
      ),
      switchMap(
        ([
          {
            payload: { reportData }
          },
          userId,
          referralCode
        ]) =>
          this.reportApiService
            .sendRequest('POST', '/v3/Report/Calculator/Amortization', {
              body: {
                ...reportData,
                parseUserId: userId,
                repCode: referralCode
              },
              responseType: 'blob'
            })
            .pipe(
              switchMap(async (blob: Blob) => {
                await this.fileService.exportDocument(
                  blob,
                  'calculator-amortization-export.pdf',
                  'Calculator amortization export'
                );
                return fromCalculatorActions.saveAmortizationReportSuccess();
              }),
              catchError((error: Error) =>
                of(fromCalculatorActions.saveAmortizationReportFailure({ payload: { error } }))
              )
            )
      )
    )
  );

  errorAlerts$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(
          fromCalculatorActions.savePITIReportFailure,
          fromCalculatorActions.saveAffordabilityReportFailure,
          fromCalculatorActions.saveAmortizationReportFailure
        ),
        concatMap(async ({ payload: { error } }) => {
          const alert = await this.alertController.create({
            backdropDismiss: false,
            buttons: [
              {
                role: 'cancel',
                text: 'Ok'
              }
            ],
            header: 'Oops',
            message: error.message,
            subHeader: 'Save Report Error'
          });
          await alert.present();
        })
      ),
    {
      dispatch: false
    }
  );
}
